/* =========================================================================
   Nivada Grenchen — F77 MKII · 100 Years
   Dependency-free, instance-safe behaviour for the ng-f77-* sections:

     1. accessible tab switcher (arrow / Home / End keys, managed tabindex)
     2. scroll-snap carousels with functional dot pagination
     3. publishes --ng-f77-header-offset on <html> so the sticky mobile tab
        strip pins under whatever the theme header currently is

   Every root is discovered by [data-ng-f77-tabs] / [data-ng-f77-carousel] and
   marked once, so re-running boot() is idempotent. shopify:section:load and
   :unload are handled for the Theme Editor.
   ========================================================================= */
(function () {
  'use strict';

  var EDITION_ATTR = 'data-ng-f77-edition';
  var OFFSET_PROP = '--ng-f77-header-offset';

  function prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollBehavior() {
    return prefersReduced() ? 'auto' : 'smooth';
  }

  /* In-flow document position of an element. A pinned sticky element reports
     its shifted position through both offsetTop and getBoundingClientRect, so
     drop it back into flow for the measurement and restore it in the same
     frame — sticky occupies its normal-flow space, so nothing reflows visibly. */
  function documentTop(el) {
    var inline = el.style.position;
    el.style.position = 'static';
    var y = el.getBoundingClientRect().top + window.scrollY;
    el.style.position = inline;
    return y;
  }

  /* ------------------------------------------------- HEADER OFFSET ------- */
  /* The theme header is `position:fixed; top:0` once it has scrolled past its
     threshold, so the sticky tab strip has to clear its height. Measure the
     live element rather than hard-coding a number, so announcement bars,
     toolbars and logo-size changes are all accounted for. */
  var headerSelectors = [
    '.site-header',
    '.header-wrapper',
    '#shopify-section-header',
    '[data-header-height]'
  ];

  function measureHeaderOffset() {
    var h = 0;
    for (var i = 0; i < headerSelectors.length; i++) {
      var el = document.querySelector(headerSelectors[i]);
      if (el && el.offsetHeight) { h = el.offsetHeight; break; }
    }
    // The Theme Editor renders an admin bar of its own; never go negative.
    document.documentElement.style.setProperty(OFFSET_PROP, Math.max(0, Math.round(h)) + 'px');
  }

  var offsetFrame = null;
  function scheduleHeaderOffset() {
    if (offsetFrame) return;
    offsetFrame = requestAnimationFrame(function () {
      offsetFrame = null;
      measureHeaderOffset();
    });
  }

  /* --------------------------------------------------------- TABS -------- */
  function initTabs(root) {
    if (root.ngF77TabsReady) return;
    root.ngF77TabsReady = true;

    var list = root.querySelector('[role="tablist"]');
    if (!list) return;

    var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    var panels = tabs.map(function (tab) {
      var id = tab.getAttribute('aria-controls');
      return id ? root.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id)) : null;
    });

    // The sticky wrapper is what actually pins; fall back to the strip itself.
    var bar = list.closest('.f77-tabs-bar') || list;

    function select(index, moveFocus, scrollToTop) {
      tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        var panel = panels[i];
        if (panel) panel.hidden = !selected;
      });

      var key = tabs[index].getAttribute('data-key') || '';
      list.setAttribute('data-active', key);
      list.style.setProperty('--f77-tab-index', index);
      // The shared sections below live in their own Shopify sections, so the
      // active edition is published on <html> for them to read.
      document.documentElement.setAttribute(EDITION_ATTR, key);

      if (moveFocus) tabs[index].focus({ preventScroll: true });

      // Re-measure any carousel that has just become visible.
      var panel = panels[index];
      if (panel) {
        panel.querySelectorAll('[data-ng-f77-carousel]').forEach(function (c) {
          if (c.ngF77Sync) c.ngF77Sync();
        });
      }

      // Switching lands you mid-way through a different panel, so bring the
      // strip back to the top and start the new panel from its beginning.
      if (scrollToTop) alignToTabs();
    }

    /* Scroll so the tab strip sits at the top of the viewport — under the
       storefront header when the strip is sticky (its resolved `top`). */
    function alignToTabs() {
      var stickyTop = parseFloat(window.getComputedStyle(bar).top);
      if (isNaN(stickyTop)) stickyTop = 0;
      var target = Math.max(0, Math.round(documentTop(bar) - stickyTop));
      if (Math.abs(window.scrollY - target) < 2) return;
      window.scrollTo({ top: target, behavior: scrollBehavior() });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(i, false, true);
      });

      tab.addEventListener('keydown', function (e) {
        var next = null;
        switch (e.key) {
          case 'ArrowLeft': next = (i - 1 + tabs.length) % tabs.length; break;
          case 'ArrowRight': next = (i + 1) % tabs.length; break;
          case 'Home': next = 0; break;
          case 'End': next = tabs.length - 1; break;
          default: return;
        }
        e.preventDefault();
        select(next, true, true);
      });
    });

    // Establish the initial state from the markup.
    var initial = -1;
    tabs.forEach(function (t, i) {
      if (initial < 0 && t.getAttribute('aria-selected') === 'true') initial = i;
    });
    select(initial < 0 ? 0 : initial, false, false);

    // The Theme Editor selects a block; reveal the panel it belongs to.
    root.ngF77SelectPanel = function (blockId) {
      for (var i = 0; i < panels.length; i++) {
        var p = panels[i];
        if (!p) continue;
        if (p.getAttribute('data-block-id') === blockId || p.querySelector('[data-block-id="' + blockId + '"]')) {
          select(i, false, false);
          return;
        }
      }
    };
  }

  /* ---------------------------------------------------- CAROUSELS -------- */
  function initCarousel(el) {
    if (el.ngF77CarouselReady) return;
    el.ngF77CarouselReady = true;

    var track = el.querySelector('[data-ng-f77-carousel-track]');
    var dotsHost = el.querySelector('[data-ng-f77-carousel-dots]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) { if (dotsHost) dotsHost.remove(); return; }

    // Start from an empty rail: the Theme Editor can hand back markup that
    // already contains a previously generated set of dots.
    if (dotsHost) dotsHost.textContent = '';

    // Interface strings come from Liquid so they stay translatable.
    var template = el.getAttribute('data-slide-label') || 'Slide [index] of [total]';
    function slideLabel(i) {
      return template
        .replace('[index]', String(i + 1))
        .replace('[total]', String(slides.length));
    }

    var dots = [];
    if (dotsHost) {
      slides.forEach(function (slide, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'f77-carousel__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.setAttribute('aria-label', slideLabel(i));
        dot.addEventListener('click', function () {
          goTo(i);
        });
        dotsHost.appendChild(dot);
        dots.push(dot);
      });
    }

    function goTo(i) {
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: scrollBehavior() });
    }

    function current() {
      var w = track.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w)));
    }

    function sync() {
      var i = current();
      dots.forEach(function (dot, n) {
        dot.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
    }

    var frame = null;
    track.addEventListener('scroll', function () {
      if (frame) return;
      frame = requestAnimationFrame(function () { frame = null; sync(); });
    }, { passive: true });

    track.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      var i = current() + (e.key === 'ArrowRight' ? 1 : -1);
      i = Math.max(0, Math.min(slides.length - 1, i));
      goTo(i);
    });

    el.ngF77Sync = sync;
    el.ngF77GoTo = goTo;
    el.ngF77Index = function (slide) { return slides.indexOf(slide); };

    /* Autoplay is opt-in: a carousel advances only when its markup carries
       data-autoplay="<ms>". It never runs against a reduced-motion request,
       and it yields to the visitor — hover, focus and touch pause it, and a
       hidden tab stops it entirely. */
    var delay = parseInt(el.getAttribute('data-autoplay'), 10);
    if (delay > 0 && !prefersReduced()) {
      var timer = null;

      var stop = function () {
        if (timer) { clearInterval(timer); timer = null; }
      };
      var start = function () {
        if (timer || document.hidden) return;
        timer = setInterval(function () {
          goTo((current() + 1) % slides.length);
        }, delay);
      };

      ['mouseenter', 'focusin', 'pointerdown', 'touchstart'].forEach(function (type) {
        el.addEventListener(type, stop, { passive: true });
      });
      ['mouseleave', 'focusout'].forEach(function (type) {
        el.addEventListener(type, start, { passive: true });
      });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });

      el.ngF77StopAutoplay = stop;
      start();
    }

    sync();
  }

  /* ----------------------------------------------------- KLAVIYO --------- */
  /* A call to action set to "Klaviyo form popup" is rendered as an ordinary
     link whose href is #klaviyo-<form id>. Nothing else about the button
     changes, so the markup stays the same whichever mode it is in — this
     listener is what turns the click into an onsite form. Delegated from the
     document and scoped to .ng-f77-page, so it never touches theme links and
     needs no per-button wiring. */
  var KLAVIYO_HREF = '#klaviyo-';

  function klaviyoFormId(link) {
    var href = link.getAttribute('href') || '';
    var at = href.indexOf(KLAVIYO_HREF);
    return at < 0 ? '' : href.slice(at + KLAVIYO_HREF.length);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var link = e.target.closest('.ng-f77-page a[href*="' + KLAVIYO_HREF + '"]');
    if (!link) return;

    var id = klaviyoFormId(link);
    if (!id) return;

    e.preventDefault();
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', id]);
  });

  /* A product card is rendered by a snippet, so its button's href cannot be
     built in Liquid. The grid publishes which card positions should open a
     form — "0:WmqPnJ,2:AbC123" — and their link is retargeted here, after
     which the delegated handler above treats them like any other Klaviyo CTA. */
  function initKlaviyoCards(grid) {
    if (grid.ngF77KlaviyoReady) return;
    grid.ngF77KlaviyoReady = true;

    var map = grid.getAttribute('data-ng-f77-klaviyo-cards') || '';
    if (!map) return;

    var cards = grid.children;
    map.split(',').forEach(function (pair) {
      if (!pair) return;
      var at = pair.indexOf(':');
      if (at < 1) return;
      var card = cards[parseInt(pair.slice(0, at), 10)];
      var id = pair.slice(at + 1);
      if (!card || !id) return;
      // only a card showing a link; one showing an add-to-cart form is left alone
      var link = card.querySelector('a.f77-watch__cta') || card.querySelector('a.f77-btn');
      if (link) link.setAttribute('href', KLAVIYO_HREF + id);
    });
  }

  /* ------------------------------------------------------------ BOOT ----- */
  function boot(scope) {
    var host = scope || document;
    if (host.matches && host.matches('[data-ng-f77-tabs]')) initTabs(host);
    host.querySelectorAll('[data-ng-f77-tabs]').forEach(initTabs);
    if (host.matches && host.matches('[data-ng-f77-carousel]')) initCarousel(host);
    host.querySelectorAll('[data-ng-f77-carousel]').forEach(initCarousel);
    if (host.matches && host.matches('[data-ng-f77-klaviyo-cards]')) initKlaviyoCards(host);
    host.querySelectorAll('[data-ng-f77-klaviyo-cards]').forEach(initKlaviyoCards);
    measureHeaderOffset();
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    boot(document);

    window.addEventListener('resize', scheduleHeaderOffset, { passive: true });
    window.addEventListener('orientationchange', scheduleHeaderOffset, { passive: true });
    // The theme's header swaps to `position:fixed` past a scroll threshold and
    // hides its toolbar, which changes its height.
    window.addEventListener('scroll', scheduleHeaderOffset, { passive: true });
    window.addEventListener('load', scheduleHeaderOffset);

    var sync = function () {
      document.querySelectorAll('[data-ng-f77-carousel]').forEach(function (c) {
        if (c.ngF77Sync) c.ngF77Sync();
      });
    };
    window.addEventListener('resize', sync, { passive: true });
  });

  /* --------------------------------------------------- THEME EDITOR ------ */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector) boot(e.target);
    scheduleHeaderOffset();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector) {
      // If the tabs section itself went away, drop the edition flag it published.
      if (e.target.querySelector('[data-ng-f77-tabs]')) {
        document.documentElement.removeAttribute(EDITION_ATTR);
      }
      // A detached carousel must not keep an autoplay interval alive.
      e.target.querySelectorAll('[data-ng-f77-carousel]').forEach(function (c) {
        if (c.ngF77StopAutoplay) c.ngF77StopAutoplay();
      });
    }
    scheduleHeaderOffset();
  });

  document.addEventListener('shopify:section:reorder', scheduleHeaderOffset);

  document.addEventListener('shopify:block:select', function (e) {
    // A slide block: bring its carousel to that slide and hold it there.
    var carousel = e.target && e.target.closest ? e.target.closest('[data-ng-f77-carousel]') : null;
    if (carousel && carousel.ngF77GoTo) {
      if (carousel.ngF77StopAutoplay) carousel.ngF77StopAutoplay();
      var slide = e.target.closest('[data-ng-f77-carousel-track] > *');
      var index = slide ? carousel.ngF77Index(slide) : -1;
      if (index > -1) carousel.ngF77GoTo(index);
    }

    var root = e.target && e.target.closest ? e.target.closest('[data-ng-f77-tabs]') : null;
    if (!root) {
      root = document.querySelector('[data-ng-f77-tabs]');
      if (!root || !root.contains(e.target)) return;
    }
    var id = e.target.getAttribute('data-block-id') || e.detail && e.detail.blockId;
    if (root.ngF77SelectPanel && id) root.ngF77SelectPanel(id);
  });



  window.addEventListener("klaviyoForms", function (e) {
    if (e.detail.type == 'submit') {
      // add Facebook tracking pixel code here
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function () {
          n.callMethod ?
            n.callMethod.apply(n, arguments) : n.queue.push(arguments)
        };
        if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
        n.queue = []; t = b.createElement(e); t.async = !0;
        t.src = v; s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s)
      }(window, document, 'script',
        'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '225981648771540');
      fbq('track', 'Lead');
    }
  });
})();
